import React, { useState, useEffect, useContext } from "react";
import {
  View,
  FlatList,
  TouchableOpacity,
  ScrollView,
  Platform,
  RefreshControl,
  PixelRatio} from "react-native";
import AntDesign from "@expo/vector-icons/AntDesign";
import { ApiService, setAuthToken } from "../../../service/ApiService";
import FormattedText from "../../../components/FormattedText";
import SkeletonLoader from "../../../components/SkeletonLoader";
import commonStyles from "../../../styles/commonStyles";
import OverflowTooltipText from "../../../components/OverflowTooltipText";
import {
 
  InvestmentModel} from "../../../types/IInvestmentTypes";
import { getUserIdToUse } from "../../../utils/getUserIdToUse";
import { useIsFocused } from "@react-navigation/native";
import { RefreshContext } from "../../../contexts/refreshContext";
import { SafeAreaView } from "react-native-safe-area-context";
import AppText from "../../../components/AppText";

interface InvestmentPageProps {
  navigation: {
    navigate: (screen: string, params?: object) => void;
    goBack: () => void;
  };
}

const InvestmentPage: React.FC<InvestmentPageProps> = ({ navigation }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [investments, setInvestments] = useState<InvestmentModel[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const { refreshTrigger } = useContext(RefreshContext);
  const isFocused = useIsFocused();
  const fontScale = PixelRatio.getFontScale();
  const isFontScaleLarge = fontScale >= 1.3;
  let userIdToUse: string | null;

  const fetchInvestmentData = async () => {
    setLoading(true);
    try {
      userIdToUse = await getUserIdToUse();
      console.log("Using User ID:", userIdToUse);
      const response = await ApiService.get<InvestmentModel[]>(
        `portal/partnerinvestment/${userIdToUse}`
      );

      const data = response.data;


      data.sort((a, b) => b.investment - a.investment);
      setInvestments(data);

      setLoading(false);
    } catch (error) {
      setError("Error fetching data");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isFocused && refreshTrigger > 0) {
      setAuthToken();

      fetchInvestmentData();
    }
  }, [refreshTrigger]);

  const getTotalForColumn = (columnName: string): number => {
    let total = 0;
    for (const record of investments) {
      total += (record as unknown as Record<string, number>)[columnName] || 0;
    }
    return total;
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleRefresh = () => {
    fetchInvestmentData();
  };

  const renderInvestmentItem = ({ item }: { item: InvestmentModel }) => (
    <View
      style={[
        commonStyles.subContainer,
        commonStyles.p14,
        commonStyles.mt20,
        commonStyles.mlar2,
      ]}
    >
      <TouchableOpacity
        testID={`EntityName-${item.id}`}
        onPress={() => navigation.navigate("InvestmentDetails", { item })}
      >
        <View style={commonStyles.cardItemblock}>
          <OverflowTooltipText
            testID={`entity-value-${item.id}`}
            text={item.entityName}
            style={commonStyles.detailsSubHeader}
            onPress={() => navigation.navigate("InvestmentDetails", { item })}
          />
          <AntDesign
            name="arrow-right"
            size={22}
            color="#666"
            marginBottom={4}
            testID={`arrow-right-icon-${item.id}`}
          />
        </View>
        <View style={commonStyles.detailsLabelContainer}>
          <View style={commonStyles.detailsGroupContainer}>
            <AppText style={commonStyles.detailsLabel} testID={`inv:${item.id}`}>
              Inv:{" "}
            </AppText>
            <FormattedText
              testID={`inv-value-${item.id}`}
              value={item.investment}
              type="currency"
              decimalPlaces={isFontScaleLarge ? 0 : 2}
              style={commonStyles.detailsValue}
            />
          </View>
          <View style={commonStyles.detailsGroupContainer}>
            <AppText style={commonStyles.detailsLabel} testID={`cash:${item.id}`}>
              Cash:{" "}
            </AppText>
            <FormattedText
              testID={`cash-value-${item.id}`}
              value={item.cash}
              type="currency"
              decimalPlaces={isFontScaleLarge ? 0 : 2}
              style={commonStyles.detailsValue}
            />
          </View>
          <View style={commonStyles.detailsGroupContainer}>
            <AppText style={commonStyles.detailsLabel} testID={`loan:${item.id}`}>
              Initial Loan:{" "}
            </AppText>
            <FormattedText
              testID={`loan-value-${item.id}`}
              value={item.loan}
              type="currency"
              decimalPlaces={isFontScaleLarge ? 0 : 2}
              style={commonStyles.detailsValue}
            />
          </View>
          <View style={commonStyles.detailsGroupContainer}>
            <AppText style={commonStyles.detailsLabel} testID={`qemi:${item.id}`}>
              QEMI:{" "}
            </AppText>

            <FormattedText
              testID={`qemi-val:${item.id}`}
              value={item.revisedQemi}
              decimalPlaces={isFontScaleLarge ? 0 : 2}
              type="currency"
              style={commonStyles.detailsValue}
            />
          </View>
        </View>
      </TouchableOpacity>
    </View>
  );

  return (
    <SafeAreaView edges={[ "bottom", "left", "right"]} style={[commonStyles.container, commonStyles.screenBottomGap]}>
      <View style={[commonStyles.headerContainer, { paddingBottom: 5 }]}>
        <AppText testID="investment-header" style={commonStyles.header}>
          Your Investments
        </AppText>
      </View>
      <ScrollView
        testID="scroll-view"
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }
      >
        <View style={commonStyles.container}>
          <View
            style={[
              commonStyles.subContainer,
              commonStyles.p16,
              commonStyles.mb5,
            ]}
            testID="summary-section"
          >
            <TouchableOpacity onPress={toggleExpand}>
              <View style={commonStyles.cardItemblock}>
                <AppText
                  testID="summary-header"
                  style={commonStyles.summaryHeader}
                >
                  Summary
                </AppText>
                <AntDesign
                  name={isExpanded ? "up" : "down"}
                  size={24}
                  color="#666"
                  testID={`arrow-icon-${isExpanded ? "up" : "down"}`}
                />
              </View>
            </TouchableOpacity>
            {isExpanded && (
              <View
                testID="summary-content"
                style={{ marginTop: 0, paddingTop: 0 }}
              >
                <View style={commonStyles.summaryItem}>
                  <AppText
                    testID="total-investment"
                    style={commonStyles.commonFont}
                  >
                    Total Investment
                  </AppText>
                  <FormattedText
                    testID="total-investment-value"
                    value={getTotalForColumn("investment")}
                    type="currency"
                    style={commonStyles.summaryAmount}
                    loading={loading}
                  />
                </View>
                <View style={commonStyles.summaryItem}>
                  <AppText testID="total-cash" style={commonStyles.commonFont}>
                    Total Cash
                  </AppText>
                  <FormattedText
                    testID="total-cash-value"
                    value={getTotalForColumn("totalDownpayment")}
                    type="currency"
                    style={commonStyles.summaryAmount}
                    loading={loading}
                  />
                </View>
                <View style={commonStyles.summaryItem}>
                  <AppText testID="total-loan" style={commonStyles.commonFont}>
                    Total Loan
                  </AppText>
                  <FormattedText
                    testID="total-loan-value"
                    value={getTotalForColumn("loan")}
                    type="currency"
                    style={commonStyles.summaryAmount}
                    loading={loading}
                  />
                </View>
                <TouchableOpacity
                  testID="qemi-arrow"
                  style={commonStyles.summaryItem}
                  onPress={() =>
                    navigation.navigate("CurrentQEMIDetails", {
                      item: investments})
                  }
                >
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 8}}
                  >
                    <AppText testID="current-qemi" style={commonStyles.commonFont}>
                      Current QEMI
                    </AppText>
                    <AntDesign
                      name="arrow-right"
                      size={15}
                      color="#666"
                      testID="current-qemi-arrow"
                    />
                  </View>

                  <FormattedText
                    testID="current-qemi-value"
                    value={getTotalForColumn("revisedQemi")}
                    type="currency"
                    style={commonStyles.summaryAmount}
                    loading={loading}
                  />
                </TouchableOpacity>

              </View>
            )}
          </View>

          <AppText testID="details" style={commonStyles.detailsHeader}>
            Details
          </AppText>

          {loading ? (
            <View testID="skeleton-loader">
              <SkeletonLoader rows={10}></SkeletonLoader>
            </View>
          ) : (
            <FlatList
              data={investments}
              renderItem={renderInvestmentItem}
              keyExtractor={(item) => item.id.toString()}
              contentContainerStyle={commonStyles.pb16}
              scrollEnabled={Platform.OS == "web" ? true : false}
            />
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

export default InvestmentPage;
